import React, { useEffect, useMemo, useState } from 'react'
import { format } from 'date-fns'
import { DataTable, Button, Input, PageHeader } from '@/shared/ui'
import type { Column, ClientTariffListItemDTO, ClientTariffWriteRequest } from '@/shared/api'
import {
  useClientTariffs,
  useCreateClientTariff,
  useUpdateClientTariff,
  useSetClientTariffActive,
  useDeleteClientTariff,
  useRegions,
  usePagination,
  useDebounce,
} from '@/shared/hooks'
import { useAuth } from '@/shared/contexts'
import { ClientTariffFormModal, KIND_LABELS } from './ClientTariffFormModal'
import { ClientTariffPreviewModal } from './ClientTariffPreviewModal'

const STATUS_LABELS: Record<number, string> = {
  0: 'Черновик',
  1: 'Активен',
  2: 'Приостановлен',
}

const STATUS_CLASSES: Record<number, string> = {
  0: 'bg-gray-100 text-gray-800',
  1: 'bg-success-50 text-success-700',
  2: 'bg-warning-50 text-warning-700',
}

const STATUS_OPTIONS = [
  { value: 0, label: 'Черновик' },
  { value: 1, label: 'Активен' },
  { value: 2, label: 'Приостановлен' },
]

const KIND_OPTIONS = [
  { value: 1, label: 'Общий' },
  { value: 2, label: 'Специальный' },
]

const formatPeriod = (from: number | null, to: number | null): string => {
  const fmt = (ts: number) => format(new Date(ts * 1000), 'dd.MM.yyyy')
  if (from && to) return `${fmt(from)} — ${fmt(to)}`
  if (from) return `с ${fmt(from)}`
  if (to) return `по ${fmt(to)}`
  return '—'
}

const StatusBadge: React.FC<{ status: number }> = ({ status }) => (
  <span
    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
      STATUS_CLASSES[status] ?? 'bg-gray-100 text-gray-800'
    }`}
  >
    {STATUS_LABELS[status] ?? 'Неизвестно'}
  </span>
)

interface FormTarget {
  id: number | null
  readOnly: boolean
}

export const ClientTariffsPage: React.FC = () => {
  const { hasPermission } = useAuth()
  const { page, limit, setPage } = usePagination({ initialLimit: 50 })

  const [regionId, setRegionId] = useState<number | undefined>(undefined)
  const [status, setStatus] = useState<number | undefined>(undefined)
  const [kind, setKind] = useState<number | undefined>(undefined)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 500)
  const [formTarget, setFormTarget] = useState<FormTarget | null>(null)
  const [previewOpen, setPreviewOpen] = useState(false)

  useEffect(() => {
    setPage(1)
  }, [regionId, status, kind, debouncedSearch, setPage])

  const params = useMemo(
    () => ({
      page,
      limit,
      ...(regionId !== undefined ? { regionId } : {}),
      ...(status !== undefined ? { status } : {}),
      ...(kind !== undefined ? { kind } : {}),
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
    }),
    [page, limit, regionId, status, kind, debouncedSearch]
  )

  const { data, isLoading } = useClientTariffs(params)
  const { data: regions } = useRegions(true)

  const regionNames = useMemo(() => {
    const map = new Map<number, string>()
    for (const region of regions ?? []) map.set(region.id, region.name)
    return map
  }, [regions])

  const create = useCreateClientTariff()
  const update = useUpdateClientTariff()
  const setActive = useSetClientTariffActive()
  const remove = useDeleteClientTariff()

  const handleSubmit = async (body: ClientTariffWriteRequest) => {
    try {
      if (formTarget?.id) {
        await update.mutateAsync({ id: formTarget.id, body })
      } else {
        await create.mutateAsync(body)
      }
      setFormTarget(null)
    } catch {
      // Ошибка уже показана тостом в хуке
    }
  }

  const handleToggleActive = async (item: ClientTariffListItemDTO) => {
    try {
      await setActive.mutateAsync({ id: item.id, active: item.status !== 1 })
    } catch {
      // Ошибка уже показана тостом в хуке
    }
  }

  const handleDelete = async (item: ClientTariffListItemDTO) => {
    if (!window.confirm(`Удалить тариф «${item.name}»? Действие необратимо.`)) return
    try {
      await remove.mutateAsync(item.id)
    } catch {
      // Ошибка уже показана тостом в хуке
    }
  }

  const columns: Column<ClientTariffListItemDTO>[] = [
    { key: 'name', title: 'Название' },
    { key: 'kind', title: 'Тип', render: (_, row) => KIND_LABELS[row.kind] ?? '—' },
    {
      key: 'region_id',
      title: 'Город',
      render: (_, row) => regionNames.get(row.region_id) ?? `#${row.region_id}`,
    },
    { key: 'base_price', title: 'Базовая стоимость', render: (_, row) => `${Number(row.base_price)} ₽` },
    {
      key: 'date_from',
      title: 'Период',
      render: (_, row) => formatPeriod(row.date_from, row.date_to),
    },
    { key: 'status', title: 'Статус', render: (_, row) => <StatusBadge status={row.status} /> },
    { key: 'rules_count', title: 'Правил', render: (_, row) => row.rules_count },
    {
      key: 'actions',
      title: 'Действия',
      render: (_, row) => (
        <div className="flex gap-2 flex-wrap" onClick={(e) => e.stopPropagation()}>
          {hasPermission('clientTariffs', 'edit') && (
            <Button
              size="sm"
              onClick={() => setFormTarget({ id: row.id, readOnly: false })}
            >
              Изменить
            </Button>
          )}
          {hasPermission('clientTariffs', 'edit') && row.status !== 1 && (
            <Button
              size="sm"
              variant="secondary"
              isLoading={setActive.isPending}
              onClick={() => {
                void handleToggleActive(row)
              }}
            >
              Активировать
            </Button>
          )}
          {hasPermission('clientTariffs', 'disable') && row.status === 1 && (
            <Button
              size="sm"
              variant="secondary"
              isLoading={setActive.isPending}
              onClick={() => {
                void handleToggleActive(row)
              }}
            >
              Приостановить
            </Button>
          )}
          {hasPermission('clientTariffs', 'disable') && (
            <Button
              size="sm"
              variant="danger"
              isLoading={remove.isPending}
              onClick={() => {
                void handleDelete(row)
              }}
            >
              Удалить
            </Button>
          )}
        </div>
      ),
    },
  ]

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <PageHeader
        title="Тарифы для юрлиц"
        description="Условия обслуживания и тарифы для клиентов платформы"
      >
        {hasPermission('clientTariffs', 'preview') && (
          <Button variant="secondary" onClick={() => setPreviewOpen(true)}>
            Предпросмотр расчёта
          </Button>
        )}
        {hasPermission('clientTariffs', 'create') && (
          <Button onClick={() => setFormTarget({ id: null, readOnly: false })}>
            Создать тариф
          </Button>
        )}
      </PageHeader>

      <div className="p-6 space-y-4">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="w-56">
            <Input
              label="Поиск"
              placeholder="Название тарифа"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">Город</label>
            <select
              className="admin-input"
              value={regionId ?? ''}
              onChange={(e) => setRegionId(e.target.value ? Number(e.target.value) : undefined)}
            >
              <option value="">Все города</option>
              {(regions ?? []).map((region) => (
                <option key={region.id} value={region.id}>
                  {region.name}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">Статус</label>
            <select
              className="admin-input"
              value={status ?? ''}
              onChange={(e) => setStatus(e.target.value ? Number(e.target.value) : undefined)}
            >
              <option value="">Все статусы</option>
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">Тип</label>
            <select
              className="admin-input"
              value={kind ?? ''}
              onChange={(e) => setKind(e.target.value ? Number(e.target.value) : undefined)}
            >
              <option value="">Все типы</option>
              {KIND_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <DataTable
          data={data?.items ?? []}
          columns={columns}
          loading={isLoading}
          emptyMessage="Тарифов с выбранными фильтрами нет"
          {...(hasPermission('clientTariffs', 'view_card')
            ? {
                onRowClick: (row: ClientTariffListItemDTO) =>
                  setFormTarget({ id: row.id, readOnly: true }),
              }
            : {})}
          pagination={
            data
              ? {
                  page: data.page,
                  limit: data.limit,
                  total: data.total,
                  pages: data.pages,
                  onPageChange: setPage,
                }
              : undefined
          }
        />
      </div>

      <ClientTariffFormModal
        isOpen={formTarget !== null}
        tariffId={formTarget?.id ?? null}
        readOnly={formTarget?.readOnly ?? false}
        onClose={() => setFormTarget(null)}
        onSubmit={handleSubmit}
        isLoading={create.isPending || update.isPending}
      />

      <ClientTariffPreviewModal
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
      />
    </div>
  )
}
